import { AiPanelTrait, DualShape } from '../../../../../types/geneCodes/AiPanelReportTypes';

// The legacy impact order (temp/gene.codes/js/pubscripts1.js:347-361). `medium` and `moderate`
// are the same rank because the two spellings have always been interchangeable in stored rows.
export const IMPACT_ORDER: Record<string, number> = {
	high: 0,
	'moderate-high': 1,
	moderate: 2,
	medium: 2,
	mixed: 3,
	low: 4,
	protective: 5,
	unknown: 6,
};

export const UNKNOWN_IMPACT = 6;

// The legacy badge colours, mapped onto design tokens — never a hex literal (design-system.md §2).
// Three vocabularies land on this one badge and the legacy coloured all three: trait risk
// (genetic-report.css:354-361), recommendation priority (:317-319) and biohacking evidence level
// (:433-435). `strong` and `emerging` used to fall through to the grey default.
export const getSeverityColor = (severity?: string | null): string => {
	switch ((severity ?? '').toLowerCase()) {
		case 'high':
			return 'var(--mg-danger)';
		case 'moderate-high':
			return 'var(--mg-tag-orange)';
		case 'moderate':
		case 'medium':
			return 'var(--mg-warning)';
		case 'mixed':
		case 'emerging':
			return 'var(--mg-tag-purple)';
		case 'low':
		case 'strong':
			return 'var(--mg-success)';
		case 'protective':
			return 'var(--mg-tag-cyan)';
		default:
			return 'var(--mg-border-strong)';
	}
};

export const impactRank = (trait: DualShape<AiPanelTrait>): number => {
	if (typeof trait !== 'object' || trait === null) return UNKNOWN_IMPACT;
	const level = trait.genetic_risk_level ?? trait.severity;
	if (typeof level !== 'string') return UNKNOWN_IMPACT;
	return IMPACT_ORDER[level.toLowerCase()] ?? UNKNOWN_IMPACT;
};

// Copies before sorting and breaks ties on the original index, so the caller's array is never
// mutated and equal-impact traits keep the order the model emitted them in.
export const sortTraitsByImpact = (
	traits: Array<DualShape<AiPanelTrait>>,
): Array<DualShape<AiPanelTrait>> =>
	traits
		.map((trait, index) => ({ trait, index }))
		.sort((a, b) => impactRank(a.trait) - impactRank(b.trait) || a.index - b.index)
		.map((entry) => entry.trait);
